/**
 * @krisspy-file
 * @type page
 * @name "Analytics"
 * @title "Analytics"
 * @description "Analytics dashboard with revenue, asset performance, location and issue insights"
 * @routes ["/analytics"]
 * @layout "DashboardLayout"
 * @flowName "analytics"
 * @design "reference"
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  Calendar,
  TrendingUp,
  AlertTriangle,
  Download,
  Loader2,
  Info,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { getMerchantById } from '../services/merchants.service';
import { getLocations } from '../services/locations.service';
import { formatCurrency } from '../lib/currency';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useUrlFilters } from '../lib/useUrlFilters';
import {
  ymdInZoneToUtcStartIso,
  ymdInZoneToUtcEndIso,
  utcIsoToZonedIsoWithOffset,
  utcIsoToZonedYmd,
} from '../lib/timezone';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { downloadCsv, money, moneyOrNull, type CsvColumn } from '../lib/csv';
import { getBookkeepingRows, BOOKKEEPING_HARD_CAP, type BookkeepingRow } from '../services/bookkeeping.service';
import { useToast } from '../contexts/ToastContext';
import {
  getRevenueOverTime,
  getRevenuePerAsset,
  getLocationPerformance,
  getIssuesByProduct,
  getBlockingTimeStats,
  getBookingStatusBreakdown,
  getPaymentMethodBreakdown,
  type RevenueDataPoint,
  type AssetRevenue,
  type LocationPerformance as LocationPerf,
  type ProductIssues,
  type BlockingTimeStats,
  type PaymentChannelStats,
} from '../services/analytics.service';

// Chart color palette matching theme
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9', '#6366f1'];
const STATUS_COLORS: Record<string, string> = {
  open: '#3b82f6',
  confirmed: '#10b981',
  active: '#0ea5e9',
  completed: '#8b5cf6',
  returned: '#6366f1',
  cancelled: '#ef4444',
  pending: '#f59e0b',
};

interface DatePreset {
  labelKey: string;
  /** Used as the URL value and as the day count for last-N-days; 'custom' is special */
  value: string;
  days: number;
}

const DATE_PRESETS: DatePreset[] = [
  { labelKey: 'analytics.last7days', value: '7', days: 7 },
  { labelKey: 'analytics.last30days', value: '30', days: 30 },
  { labelKey: 'analytics.thisYear', value: '365', days: 365 },
];

function lastNDaysIsoRange(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

// YMD ↔ UTC ISO conversion is timezone-aware via lib/timezone
// (ymdInZoneToUtcStartIso / utcIsoToZonedYmd). The merchant's IANA timezone
// is fetched at page load.

export default function Analytics() {
  const { accessControl, merchantId } = useAuth();
  const { formatDateShort, formatDate } = useDateFormatter();
  const { t } = useTranslation();

  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [merchantTimezone, setMerchantTimezone] = useState<string>('UTC');
  const [locations, setLocations] = useState<{ id: string; location_name: string }[]>([]);

  // URL-persisted filters: preset (one of '7' / '30' / '365' / 'custom'),
  // from / to as YYYY-MM-DD, basis ('rental' | 'booking'), location (id).
  const { getFilter, setFilter, setFilters: setUrlFilters } = useUrlFilters({
    defaults: {
      preset: '30',
      from: '',
      to: '',
      basis: 'booking',
      location: '',
    },
    storageKey: 'analytics-filters',
  });

  const presetValue = getFilter('preset') || '30';
  const fromYmd = getFilter('from');
  const toYmd = getFilter('to');
  const basis = (getFilter('basis') || 'booking') as 'booking' | 'rental';
  const selectedLocationId = getFilter('location');

  // Data states
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [assetData, setAssetData] = useState<AssetRevenue[]>([]);
  const [locationData, setLocationData] = useState<LocationPerf[]>([]);
  const [issuesData, setIssuesData] = useState<ProductIssues[]>([]);
  const [blockingData, setBlockingData] = useState<BlockingTimeStats[]>([]);
  const [statusData, setStatusData] = useState<{ status: string; count: number }[]>([]);
  const [paymentData, setPaymentData] = useState<PaymentChannelStats[]>([]);

  const dateRange = useMemo(() => {
    if (presetValue === 'custom' && fromYmd && toYmd) {
      return {
        startDate: ymdInZoneToUtcStartIso(fromYmd, merchantTimezone),
        endDate:   ymdInZoneToUtcEndIso(toYmd, merchantTimezone),
      };
    }
    const preset = DATE_PRESETS.find(p => p.value === presetValue) || DATE_PRESETS[1];
    return lastNDaysIsoRange(preset.days);
  }, [presetValue, fromYmd, toYmd, merchantTimezone]);

  const filters = useMemo(() => ({
    ...dateRange,
    locationId: selectedLocationId || undefined,
    dateBasis: basis,
  }), [dateRange, selectedLocationId, basis]);

  const selectPreset = (value: string) => {
    if (value === 'custom') {
      // Prefill from/to with the currently resolved range so the user has
      // a sensible starting point to tweak. Convert via merchant tz so the
      // YMD shown in the inputs matches the merchant's calendar.
      setUrlFilters({
        preset: 'custom',
        from: fromYmd || utcIsoToZonedYmd(dateRange.startDate, merchantTimezone),
        to:   toYmd || utcIsoToZonedYmd(dateRange.endDate, merchantTimezone),
      });
    } else {
      setUrlFilters({ preset: value, from: '', to: '' });
    }
  };

  const setCustomFrom = (value: string) => {
    setUrlFilters({ preset: 'custom', from: value, to: toYmd || value });
  };

  const setCustomTo = (value: string) => {
    setUrlFilters({ preset: 'custom', from: fromYmd || value, to: value });
  };

  // Local draft state for the date inputs. Pushing to the URL on every
  // change re-renders and remounts the input mid-edit — when the user is
  // typing into the day/month field, even a momentarily complete value
  // (e.g. typing "1" → Chrome normalises to "01") commits and the input
  // visibly resets, breaking the next keystroke. We instead debounce the
  // commit by 600ms (cancelled on each new keystroke) and force-commit on
  // blur, so the URL only updates once the user has paused or moved on.
  const [draftFrom, setDraftFrom] = useState(fromYmd);
  const [draftTo, setDraftTo] = useState(toYmd);
  useEffect(() => { setDraftFrom(fromYmd); }, [fromYmd]);
  useEffect(() => { setDraftTo(toYmd); }, [toYmd]);

  const isCommittableYmd = (v: string) =>
    v === '' || (/^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0, 4)) >= 1900);

  const fromCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const COMMIT_DEBOUNCE_MS = 600;

  const onFromChange = (v: string) => {
    setDraftFrom(v);
    if (fromCommitTimerRef.current) clearTimeout(fromCommitTimerRef.current);
    if (isCommittableYmd(v)) {
      fromCommitTimerRef.current = setTimeout(() => setCustomFrom(v), COMMIT_DEBOUNCE_MS);
    }
  };

  const onToChange = (v: string) => {
    setDraftTo(v);
    if (toCommitTimerRef.current) clearTimeout(toCommitTimerRef.current);
    if (isCommittableYmd(v)) {
      toCommitTimerRef.current = setTimeout(() => setCustomTo(v), COMMIT_DEBOUNCE_MS);
    }
  };

  const onFromBlur = () => {
    if (fromCommitTimerRef.current) clearTimeout(fromCommitTimerRef.current);
    if (draftFrom !== fromYmd && isCommittableYmd(draftFrom)) setCustomFrom(draftFrom);
  };

  const onToBlur = () => {
    if (toCommitTimerRef.current) clearTimeout(toCommitTimerRef.current);
    if (draftTo !== toYmd && isCommittableYmd(draftTo)) setCustomTo(draftTo);
  };

  // Clear pending timers on unmount so they don't try to call setUrlFilters
  // after the page is gone.
  useEffect(() => () => {
    if (fromCommitTimerRef.current) clearTimeout(fromCommitTimerRef.current);
    if (toCommitTimerRef.current) clearTimeout(toCommitTimerRef.current);
  }, []);

  const currentPresetLabelKey = useMemo(() => {
    if (presetValue === 'custom') return 'analytics.customRange';
    const preset = DATE_PRESETS.find(p => p.value === presetValue) || DATE_PRESETS[1];
    return preset.labelKey;
  }, [presetValue]);

  const handleExportBookkeeping = async () => {
    setExporting(true);
    try {
      const { data, error, capped } = await getBookkeepingRows(accessControl, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        locationId: selectedLocationId || undefined,
        dateBasis: basis,
      });
      if (error) {
        showToast(error, 'error');
        return;
      }
      if (!data || data.length === 0) {
        showToast(t('analytics.exportEmpty'), 'info');
        return;
      }

      const columns: CsvColumn<BookkeepingRow>[] = [
        { header: 'order_id', value: (r) => r.order_id },
        { header: 'booking_date', value: (r) => utcIsoToZonedIsoWithOffset(r.booking_date, merchantTimezone) },
        { header: 'rental_start', value: (r) => utcIsoToZonedIsoWithOffset(r.rental_start, merchantTimezone) },
        { header: 'rental_end', value: (r) => utcIsoToZonedIsoWithOffset(r.rental_end, merchantTimezone) },
        { header: 'returned_at', value: (r) => utcIsoToZonedIsoWithOffset(r.returned_at, merchantTimezone) },
        { header: 'state', value: (r) => r.state },
        { header: 'location', value: (r) => r.location_name },
        { header: 'customer_name', value: (r) => r.customer_name },
        { header: 'customer_email', value: (r) => r.customer_email },
        { header: 'products', value: (r) => r.products },
        { header: 'currency', value: (r) => r.currency },
        { header: 'subtotal_net', value: (r) => moneyOrNull(r.subtotal_net) },
        { header: 'discount', value: (r) => moneyOrNull(r.discount) },
        { header: 'tax_rate', value: (r) => r.tax_rate },
        { header: 'tax_amount', value: (r) => moneyOrNull(r.tax_amount) },
        { header: 'tax_included', value: (r) => (r.tax_included === null ? '' : r.tax_included ? 'true' : 'false') },
        { header: 'total_gross', value: (r) => moneyOrNull(r.total_gross) },
        { header: 'amount_paid', value: (r) => money(r.amount_paid) },
        { header: 'amount_refunded', value: (r) => money(r.amount_refunded) },
        { header: 'net_received', value: (r) => money(r.net_received) },
        { header: 'last_payment_date', value: (r) => utcIsoToZonedIsoWithOffset(r.last_payment_date, merchantTimezone) },
        { header: 'payment_channels', value: (r) => r.payment_channels },
        { header: 'deposit_amount', value: (r) => moneyOrNull(r.deposit_amount) },
        { header: 'deposit_captured', value: (r) => moneyOrNull(r.deposit_captured) },
        { header: 'deposit_status', value: (r) => r.deposit_status },
      ];

      const fromTag = utcIsoToZonedYmd(dateRange.startDate, merchantTimezone);
      const toTag = utcIsoToZonedYmd(dateRange.endDate, merchantTimezone);
      const filename = `bookings_${basis}_${fromTag}_${toTag}.csv`;
      downloadCsv(filename, data, columns);

      if (capped) {
        // Truncated download — make the user aware so they don't ship an
        // incomplete file to their accountant.
        showToast(
          t('analytics.exportCappedWarning', { count: data.length, cap: BOOKKEEPING_HARD_CAP }),
          'warning'
        );
      } else {
        showToast(t('analytics.exportSuccess', { count: data.length }), 'success');
      }
    } catch (err: any) {
      showToast(err?.message || t('analytics.exportError'), 'error');
    } finally {
      setExporting(false);
    }
  };

  // Load locations once
  useEffect(() => {
    async function loadLocations() {
      const result = await getLocations(accessControl);
      if (result.data) {
        setLocations(result.data.map((l: any) => ({ id: l.id, location_name: l.location_name })));
      }
    }
    loadLocations();
  }, [accessControl]);

  // Load merchant currency + timezone
  useEffect(() => {
    async function loadMerchant() {
      if (merchantId) {
        const result = await getMerchantById(merchantId);
        if (result.data?.currency_code) {
          setCurrencyCode(result.data.currency_code);
        }
        if (result.data?.timezone) {
          setMerchantTimezone(result.data.timezone);
        }
      }
    }
    loadMerchant();
  }, [merchantId]);

  // Load analytics data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [
          revenueResult,
          assetResult,
          locationResult,
          issuesResult,
          blockingResult,
          statusResult,
          paymentResult,
        ] = await Promise.all([
          getRevenueOverTime(accessControl, filters),
          getRevenuePerAsset(accessControl, filters),
          getLocationPerformance(accessControl, filters),
          getIssuesByProduct(accessControl),
          getBlockingTimeStats(accessControl, filters),
          getBookingStatusBreakdown(accessControl, filters),
          getPaymentMethodBreakdown(accessControl, filters),
        ]);

        if (revenueResult.data) setRevenueData(revenueResult.data);
        if (assetResult.data) setAssetData(assetResult.data);
        if (locationResult.data) setLocationData(locationResult.data);
        if (issuesResult.data) setIssuesData(issuesResult.data);
        if (blockingResult.data) setBlockingData(blockingResult.data);
        if (statusResult.data) setStatusData(statusResult.data);
        if (paymentResult.data) setPaymentData(paymentResult.data);
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [accessControl, filters]);

  // Computed metrics
  const totalBookedRevenue = revenueData.reduce((sum, d) => sum + d.revenue, 0);
  const totalCollectedRevenue = revenueData.reduce((sum, d) => sum + d.collectedRevenue, 0);
  const totalRetainedRevenue = revenueData.reduce((sum, d) => sum + d.retainedRevenue, 0);
  // Summed from the per-booking figure rather than computed as booked minus
  // collected. That subtraction treats every refund as a receivable and, now
  // that collected includes money retained on cancelled bookings (which have no
  // booked value), it can go negative outright.
  const totalOutstanding = revenueData.reduce((sum, d) => sum + d.outstandingRevenue, 0);
  const totalBookings = revenueData.reduce((sum, d) => sum + d.bookingCount, 0);
  const avgBookingValue = totalBookings > 0 ? totalCollectedRevenue / totalBookings : 0;
  const totalIssues = issuesData.reduce((sum, d) => sum + d.issueCount, 0);

  if (loading) {
    return <LoadingPlaceholder />;
  }

  // The collected-revenue subtitle carries two independent facts, so it composes
  // them rather than choosing between them.
  //
  // Outstanding is a call to action — money the merchant should chase — and must
  // never be suppressed. Retained-on-cancellation is a footnote explaining part
  // of the headline figure: real income that did not come from a completed
  // rental, named rather than folded silently into rental revenue because
  // merchants ask where it came from. An earlier version let the footnote hide
  // the action item whenever a merchant had both.
  const collectedSubtitleParts = [
    totalOutstanding > 0
      ? `${t('analytics.outstanding')} ${formatCurrency(totalOutstanding, currencyCode)}`
      : `${t('analytics.booked')} ${formatCurrency(totalBookedRevenue, currencyCode)}`,
  ];
  if (totalRetainedRevenue > 0.01) {
    collectedSubtitleParts.push(
      `${t('analytics.includesRetained')} ${formatCurrency(totalRetainedRevenue, currencyCode)}`
    );
  }

  const metrics = [
    {
      label: t('analytics.collectedRevenue'),
      value: formatCurrency(totalCollectedRevenue, currencyCode),
      subtitle: collectedSubtitleParts.join(' · '),
      icon: DollarSign,
      iconBg: 'bg-success/10',
      iconColor: 'text-success',
      badge: { text: t(currentPresetLabelKey), variant: 'badge-info' },
    },
    {
      label: t('analytics.totalBookings'),
      value: totalBookings.toString(),
      icon: Calendar,
      iconBg: 'bg-blue/10',
      iconColor: 'text-blue',
      badge: { text: t(currentPresetLabelKey), variant: 'badge-info' },
    },
    {
      label: t('analytics.avgBookingValue'),
      value: formatCurrency(avgBookingValue, currencyCode),
      icon: TrendingUp,
      iconBg: 'bg-info/10',
      iconColor: 'text-info',
      badge: { text: t('analytics.perBooking'), variant: 'badge-info' },
    },
    {
      label: t('analytics.activeIssues'),
      value: totalIssues.toString(),
      icon: AlertTriangle,
      iconBg: totalIssues > 0 ? 'bg-warning/10' : 'bg-success/10',
      iconColor: totalIssues > 0 ? 'text-warning' : 'text-success',
      badge: { text: totalIssues > 0 ? t('analytics.open') : t('analytics.clear'), variant: totalIssues > 0 ? 'badge-warning' : 'badge-success' },
    },
  ];

  const formatChannelName = (channel: string) => {
    return channel
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatStatusName = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="dashboard-container animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="dashboard-header" data-testid="analytics-header">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-primary mb-xs">{t('analytics.title')}</h1>
          <p className="text-xs text-secondary">{t('analytics.description')}</p>
        </div>
        <div className="flex flex-col gap-xs w-full sm:w-auto sm:items-end">
          {/* Row 1: Location + Date basis dropdowns side-by-side on every breakpoint */}
          <div className="flex items-center gap-xs w-full sm:w-auto">
            {/* Location Filter */}
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <select
                value={selectedLocationId}
                onChange={(e) => setFilter('location', e.target.value)}
                className="btn btn-ghost text-xs pr-lg appearance-none cursor-pointer w-full"
                style={{ paddingRight: '28px' }}
              >
                <option value="">{t('analytics.allLocations')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.location_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date basis: rental vs booking date */}
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <select
                value={basis}
                onChange={(e) => setFilter('basis', e.target.value)}
                className="btn btn-ghost text-xs pr-lg appearance-none cursor-pointer w-full"
                style={{ paddingRight: '28px' }}
                title={t('analytics.dateBasisHint')}
              >
                <option value="rental">{t('analytics.basisRental')}</option>
                <option value="booking">{t('analytics.basisBooking')}</option>
              </select>
            </div>
          </div>

          {/* Row 2: Date Range Presets */}
          <div className="analytics-date-presets">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => selectPreset(preset.value)}
                className={`btn text-xs ${preset.value === presetValue ? 'btn-primary' : 'btn-ghost'}`}
              >
                {t(preset.labelKey)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => selectPreset('custom')}
              className={`btn text-xs ${presetValue === 'custom' ? 'btn-primary' : 'btn-ghost'}`}
            >
              {t('analytics.customRange')}
            </button>
          </div>
        </div>
      </div>

      {/* Custom date range — revealed only when the Custom preset is active */}
      {presetValue === 'custom' && (
        <div className="flex items-center gap-xs flex-wrap pl-md sm:pl-0">
          <input
            type="date"
            value={draftFrom}
            onChange={(e) => onFromChange(e.target.value)}
            onBlur={onFromBlur}
            className="input text-xs py-xs"
            aria-label={t('analytics.customFrom')}
          />
          <span className="text-xs text-secondary">–</span>
          <input
            type="date"
            value={draftTo}
            onChange={(e) => onToChange(e.target.value)}
            onBlur={onToBlur}
            className="input text-xs py-xs"
            aria-label={t('analytics.customTo')}
          />
        </div>
      )}

      {/* Key Metrics */}
      <div className="dashboard-metrics-grid">
        {metrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <div key={idx} className="card">
              <div className="flex items-start justify-between mb-sm">
                <div className={`w-10 h-10 rounded-md ${metric.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${metric.iconColor}`} />
                </div>
                <span className={`badge text-xs ${metric.badge.variant}`}>
                  {metric.badge.text}
                </span>
              </div>
              <p className="text-secondary text-xs sm:text-sm mb-xs">{metric.label}</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-primary break-words">{metric.value}</p>
              {'subtitle' in metric && metric.subtitle && (
                <p className="text-xs text-muted mt-xs">{metric.subtitle}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Bookkeeping export */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-md">
          <div className="min-w-0">
            <h3 className="font-semibold text-primary mb-xs flex items-center gap-xs">
              <Download className="w-4 h-4" />
              {t('analytics.bookkeepingExportTitle')}
            </h3>
            <p className="text-sm text-secondary mb-xs">
              {t('analytics.bookkeepingExportDescription', {
                from: formatDate(dateRange.startDate),
                to: formatDate(dateRange.endDate),
                basis: t(basis === 'rental' ? 'analytics.basisRental' : 'analytics.basisBooking'),
              })}
            </p>
            <p className="text-xs text-muted flex items-start gap-xs">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{t('analytics.bookkeepingExportTip')}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleExportBookkeeping}
            disabled={exporting}
            className="btn btn-primary text-sm whitespace-nowrap flex-shrink-0"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('analytics.exporting')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {t('analytics.downloadCsv')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Revenue Over Time */}
      <div className="card">
        <h3 className="font-semibold text-primary mb-lg">{t('analytics.revenueOverTime')}</h3>
        {revenueData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatDateShort(d)}
                tick={{ fontSize: 12, fill: '#64748B' }}
              />
              <YAxis
                tickFormatter={(v) => formatCurrency(v, currencyCode, 0)}
                tick={{ fontSize: 12, fill: '#64748B' }}
                width={80}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  formatCurrency(Number(value), currencyCode),
                  name === 'collectedRevenue' ? t('analytics.collected') : name === 'revenue' ? t('analytics.booked') : t('analytics.outstandingLabel'),
                ]}
                labelFormatter={(label) => formatDateShort(label)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
              />
              <Legend
                formatter={(value) => (
                  <span style={{ fontSize: '12px', color: '#64748B' }}>
                    {value === 'collectedRevenue' ? t('analytics.collected') : value === 'revenue' ? t('analytics.booked') : value}
                  </span>
                )}
              />
              <Line
                type="monotone"
                dataKey="collectedRevenue"
                stroke="#10b981"
                strokeWidth={2}
                dot={revenueData.length <= 31}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-xl text-secondary text-sm">
            {t('analytics.noRevenueData')}
          </div>
        )}
      </div>

      {/* Revenue per Item & Location Performance */}
      <div className="dashboard-summary-grid">
        {/* Revenue per Item */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.revenuePerAsset')}</h3>
          {assetData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={assetData.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => formatCurrency(v, currencyCode, 0)}
                    tick={{ fontSize: 11, fill: '#64748B' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="productName"
                    width={120}
                    tick={{ fontSize: 11, fill: '#64748B' }}
                  />
                  <Tooltip
                    formatter={(value: any) => [formatCurrency(Number(value), currencyCode), t('analytics.revenueSlashAsset')]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Bar dataKey="revenuePerAsset" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="dashboard-table-scroll mt-md">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>{t('analytics.product')}</th>
                      <th className="text-right">{t('analytics.revPerAsset')}</th>
                      <th className="text-right">{t('analytics.collected')}</th>
                      <th className="text-right">{t('analytics.outstandingLabel')}</th>
                      <th className="text-right">{t('analytics.assets')}</th>
                      <th className="text-right">{t('analytics.rentals')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetData.slice(0, 10).map((asset) => (
                      <tr key={asset.productTypeId}>
                        <td className="font-medium">{asset.productName}</td>
                        <td className="text-right">{formatCurrency(asset.revenuePerAsset, currencyCode)}</td>
                        <td className="text-right text-secondary">{formatCurrency(asset.collectedRevenue, currencyCode)}</td>
                        <td className="text-right">
                          {asset.outstandingRevenue > 0 ? (
                            <span className="text-warning">{formatCurrency(asset.outstandingRevenue, currencyCode)}</span>
                          ) : (
                            <span className="text-secondary">-</span>
                          )}
                        </td>
                        <td className="text-right text-secondary">{asset.itemCount}</td>
                        <td className="text-right text-secondary">{asset.usageTimes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noAssetData')}</div>
          )}
        </div>

        {/* Location Performance */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.locationPerformance')}</h3>
          {locationData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={locationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="locationName"
                    tick={{ fontSize: 11, fill: '#64748B' }}
                  />
                  <YAxis
                    tickFormatter={(v) => formatCurrency(v, currencyCode, 0)}
                    tick={{ fontSize: 11, fill: '#64748B' }}
                    width={80}
                  />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      formatCurrency(Number(value), currencyCode),
                      name === 'collectedRevenue' ? t('analytics.collected') : t('analytics.outstandingLabel'),
                    ]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Bar dataKey="collectedRevenue" fill="#10b981" stackId="rev" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="outstandingRevenue" fill="#f59e0b" stackId="rev" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="dashboard-table-scroll mt-md">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>{t('analytics.location')}</th>
                      <th className="text-right">{t('analytics.collected')}</th>
                      <th className="text-right">{t('analytics.outstandingLabel')}</th>
                      <th className="text-right">{t('analytics.bookingsLabel')}</th>
                      <th className="text-right">{t('analytics.avgValue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationData.map((loc) => (
                      <tr key={loc.locationId}>
                        <td className="font-medium">{loc.locationName}</td>
                        <td className="text-right">{formatCurrency(loc.collectedRevenue, currencyCode)}</td>
                        <td className="text-right">
                          {loc.outstandingRevenue > 0 ? (
                            <span className="text-warning">{formatCurrency(loc.outstandingRevenue, currencyCode)}</span>
                          ) : (
                            <span className="text-secondary">-</span>
                          )}
                        </td>
                        <td className="text-right text-secondary">{loc.bookingCount}</td>
                        <td className="text-right text-secondary">{formatCurrency(loc.avgBookingValue, currencyCode)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noLocationData')}</div>
          )}
        </div>
      </div>

      {/* Issues by Product & Blocking Times */}
      <div className="dashboard-summary-grid">
        {/* Issues by Product */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.issuesByProduct')}</h3>
          {issuesData.length > 0 ? (
            <div className="dashboard-table-scroll">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>{t('analytics.product')}</th>
                    <th className="text-right">{t('analytics.total')}</th>
                    <th className="text-right">{t('analytics.critical')}</th>
                    <th className="text-right">{t('analytics.high')}</th>
                    <th className="text-right">{t('analytics.medium')}</th>
                    <th className="text-right">{t('analytics.low')}</th>
                  </tr>
                </thead>
                <tbody>
                  {issuesData.map((item) => (
                    <tr key={item.productTypeId}>
                      <td className="font-medium">{item.productName}</td>
                      <td className="text-right font-medium">{item.issueCount}</td>
                      <td className="text-right">
                        {item.bySeverity.critical > 0 && (
                          <span className="badge badge-danger text-xs">{item.bySeverity.critical}</span>
                        )}
                      </td>
                      <td className="text-right">
                        {item.bySeverity.high > 0 && (
                          <span className="badge badge-warning text-xs">{item.bySeverity.high}</span>
                        )}
                      </td>
                      <td className="text-right text-secondary">{item.bySeverity.medium || '-'}</td>
                      <td className="text-right text-secondary">{item.bySeverity.low || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noOpenIssues')}</div>
          )}
        </div>

        {/* Blocking Times */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.blockingTimesByReason')}</h3>
          {blockingData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={blockingData}
                    dataKey="count"
                    nameKey="reason"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {blockingData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any) => [value, name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend
                    formatter={(value) => <span style={{ fontSize: '12px', color: '#64748B' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="dashboard-table-scroll mt-md">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>{t('analytics.reason')}</th>
                      <th className="text-right">{t('analytics.count')}</th>
                      <th className="text-right">{t('analytics.totalDays')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockingData.map((bt) => (
                      <tr key={bt.reason}>
                        <td className="font-medium">{formatChannelName(bt.reason)}</td>
                        <td className="text-right text-secondary">{bt.count}</td>
                        <td className="text-right text-secondary">{bt.totalDays}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noBlockingTimes')}</div>
          )}
        </div>
      </div>

      {/* Booking Status & Payment Methods */}
      <div className="dashboard-summary-grid">
        {/* Booking Status Breakdown */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.bookingStatusBreakdown')}</h3>
          {statusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {statusData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={STATUS_COLORS[entry.status] || CHART_COLORS[idx % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any) => [value, formatStatusName(String(name))]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend
                    formatter={(value) => (
                      <span style={{ fontSize: '12px', color: '#64748B' }}>{formatStatusName(value)}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-sm mt-md">
                {statusData.map((entry) => {
                  const total = statusData.reduce((s, e) => s + e.count, 0);
                  const pct = total > 0 ? Math.round((entry.count / total) * 100) : 0;
                  return (
                    <div key={entry.status} className="flex items-center justify-between">
                      <div className="flex items-center gap-sm">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: STATUS_COLORS[entry.status] || '#64748B' }}
                        />
                        <span className="text-sm text-secondary">{formatStatusName(entry.status)}</span>
                      </div>
                      <div className="flex items-center gap-sm">
                        <span className="text-sm font-medium text-primary">{entry.count}</span>
                        <span className="text-xs text-muted">({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noBookings')}</div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="card">
          <h3 className="font-semibold text-primary mb-lg">{t('analytics.paymentMethods')}</h3>
          {paymentData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={paymentData}
                    dataKey="totalAmount"
                    nameKey="channel"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {paymentData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      formatCurrency(Number(value), currencyCode),
                      formatChannelName(String(name)),
                    ]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend
                    formatter={(value) => (
                      <span style={{ fontSize: '12px', color: '#64748B' }}>{formatChannelName(value)}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-sm mt-md">
                {paymentData.map((entry, idx) => {
                  const totalAmt = paymentData.reduce((s, e) => s + e.totalAmount, 0);
                  const pct = totalAmt > 0 ? Math.round((entry.totalAmount / totalAmt) * 100) : 0;
                  return (
                    <div key={entry.channel} className="flex items-center justify-between">
                      <div className="flex items-center gap-sm">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                        />
                        <span className="text-sm text-secondary">{formatChannelName(entry.channel)}</span>
                      </div>
                      <div className="flex items-center gap-sm">
                        <span className="text-sm font-medium text-primary">
                          {formatCurrency(entry.totalAmount, currencyCode)}
                        </span>
                        <span className="text-xs text-muted">({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-xl text-secondary text-sm">{t('analytics.noPaymentData')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
